import React, { useEffect, useState } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';

import { type TypeDataset, type TypeIFS } from '../../common/dataTypes';
import { createTanstackTable } from '../../common/utils';
import { type IfsStatus } from '../../types';
import cn from '../../utils/cn';
import SuccessDialog from '../SuccessDialog';
import TanstackTable from '../TanstackTable';

import AlertDialog from './AlertDialog';

type IFSTableData = {
  inputFile: string | null;
  status: IfsStatus;
  importDate: string | null;
};

const defaultData = [] as IFSTableData[];

const columnHelper = createColumnHelper<IFSTableData>();

const columns = [
  columnHelper.accessor('inputFile', {
    id: 'inputFile',
    cell: (info) => (
      <div className="text-start flex flex-wrap break-all">
        {info.getValue()}
      </div>
    ),
    header: () => <div className="flex text-nowrap text-start">Input File</div>,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    cell: (info) => {
      const status = info.getValue();

      const StatusLabel = ({
        label,
        value,
      }: {
        value: 'imported' | 'fail' | 'not-imported';
        label: string;
      }) => (
        <div className="flex justify-start">
          <p
            className={cn(
              'w-fit rounded-full capitalize bg-[#E1FBE7] px-6 py-1 text-green-600',
              {
                'bg-[#E1FBE7] text-green-600': value === 'imported',
                'text-red-600 bg-[#FFF5F2]': value === 'fail',
                'text-[#666] bg-gray-100': value === 'not-imported',
              }
            )}
          >
            {label}
          </p>
        </div>
      );

      switch (status) {
        case 'imported':
          return <StatusLabel value="imported" label="Imported" />;
        case 'fail':
          return <StatusLabel value="fail" label="Fail" />;
        case 'not-imported':
          return <StatusLabel value="not-imported" label="Not Imported" />;
      }
    },
    header: () => <div className="text-start">Status</div>,
  }),
  columnHelper.accessor('importDate', {
    id: 'importDate',
    cell: (info) => (
      <p className=" text-[#333] py-2">
        {info.getValue()
          ? format(info.getValue() as string, 'y/MM/dd hh:mm a')
          : '--'}
      </p>
    ),
    sortingFn: (a, b) =>
      new Date(a.original.importDate as string).getTime() -
      new Date(b.original.importDate as string).getTime(),
    header: () => (
      <div className="flex w-full shrink text-start">Import Date</div>
    ),
  }),
];

const ReviewIFSTable = ({
  projectId,
  dataset,
  ifs,
  isLoading,
}: {
  projectId: number;
  dataset: TypeDataset;
  ifs: TypeIFS;
  isLoading: boolean;
}) => {
  const [data, setData] = useState<IFSTableData[]>(defaultData);

  const [error, setError] = useState<{
    content: React.ReactNode;
  } | null>(null);

  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);

  const table = createTanstackTable<IFSTableData>({
    data,
    columns,
  });

  useEffect(() => {
    if (ifs.files) {
      setData(
        ifs.files.map((file) => ({
          inputFile: file.name,
          status: file.is_imported ? 'imported' : 'not-imported',
          importDate: file.imported_at,
          import: null,
        }))
      );
    }
  }, [ifs]);

  return (
    <div className="space-y-3">
      <TanstackTable
        table={table}
        isLoading={isLoading}
        emptyState={
          'No IFS data available. Please generate and upload your files.'
        }
      />
      <div className="h-4" />
      <AlertDialog
        title="Error Found: Please Review"
        open={openErrorDialog}
        content={error?.content}
        handleOnClose={() => {
          setOpenErrorDialog(false);
        }}
      />
      <SuccessDialog
        content="Input File Set imported successfully."
        open={openSuccessDialog}
        handleOnClose={() => {
          setOpenSuccessDialog(false);
        }}
      />
    </div>
  );
};

export default ReviewIFSTable;
